import type {
  ActionBody,
  ActionResponse,
  MatchSnapshot,
} from "./types";

// All requests go through Phoenix. Cookies carry the session, so every
// call sets `credentials: "include"`. Phoenix returns `{ error }` on
// failure → ApiError.

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// Phoenix's `detail` can be a string, atom-stringified scalar, OR a
// map (e.g. `{exit_code, tail}` from a subprocess-runner failure). The
// old code did `String(v)` everywhere, which turned objects into
// `"[object Object]"`. JSON-stringify objects so the user sees the
// shape, with the `tail` field broken out inline because it's the
// load-bearing one in subprocess errors.
function formatChangesetErrors(errors: Record<string, unknown>): string {
  return Object.entries(errors)
    .map(([field, value]) => {
      const messages = Array.isArray(value)
        ? value.map(String).join(", ")
        : String(value);
      return `${field}: ${messages}`;
    })
    .join("; ");
}

function formatDetail(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (typeof v === "object") {
    const obj = v as Record<string, unknown>;
    if (typeof obj.tail === "string") {
      // `tail` is usually the last few hundred chars of subprocess
      // output — the actually-useful diagnostic.
      const prefix = Object.entries(obj)
        .filter(([k]) => k !== "tail")
        .map(([k, vv]) => `${k}=${JSON.stringify(vv)}`)
        .join(" ");
      return prefix ? `${prefix}\n${obj.tail}` : (obj.tail as string);
    }
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

// CSRF token plumbing. The SPA fetches a token at boot from
// /api/csrf (server plants a session cookie and returns the token).
// Every state-changing request sends it back as `x-csrf-token`.
// /api/me and /api/login responses also refresh the token so it
// stays valid for the lifetime of the session.
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

// Banned-user redirect plumbing. App.tsx registers a callback that
// `navigate("/banned")`s; any 403 with `{error: "banned"}` fires it
// before throwing. Module-level so `client.ts` doesn't depend on
// react-router, mirroring the CSRF token pattern above.
let bannedHandler: (() => void) | null = null;

export function setBannedHandler(fn: (() => void) | null): void {
  bannedHandler = fn;
}

// Capacity-queue handler. Any login / guest attempt that comes back
// 409 `{error: "capacity"}` fires this so the SPA can navigate to
// `/full` with the queue category. Same module-level pattern as the
// banned handler above.
type CapacityPayload = {
  queue: "user" | "guest";
  ticket: string;
  position: number;
};
let capacityHandler: ((p: CapacityPayload) => void) | null = null;

export function setCapacityHandler(
  fn: ((p: CapacityPayload) => void) | null,
): void {
  capacityHandler = fn;
}

// Catalog-cache invalidator. Registered by `api/useCatalog.ts` at
// module load (same pattern as the banned handler — registration
// instead of an import avoids a client ↔ useCatalog cycle). Mutations
// whose responses imply newly owned cards (pack opens, shop card
// buys, admin grants) call `invalidateCatalog()` so the shared
// catalog + collection cache refetches in the background.
let catalogInvalidator: (() => void) | null = null;

export function setCatalogInvalidator(fn: (() => void) | null): void {
  catalogInvalidator = fn;
}

function invalidateCatalog(): void {
  if (catalogInvalidator) catalogInvalidator();
}

// ---- Me cache + subscribers ----
//
// Single source of truth for the SPA's view of the logged-in user.
// Every `getMe()` call repopulates this cache and notifies every
// subscriber. Mutating endpoints that change persistent user state
// (cheats, item use, sleep, password change, …) should also call
// `refreshMe()` so the AppHeader's HP chip and currency chips reflect
// the change without the user having to reload. Returning a fresh
// MeResponse from each mutation isn't enough — the source of truth
// has to live above any individual page.
//
// Forward-declare the listener type; the `User` interface below
// satisfies the parameter shape via structural typing at call sites.
type MeListener = (user: MeUser | null) => void;
type MeUser = {
  id: number;
  username: string;
  is_admin: boolean;
  is_guest: boolean;
  is_banned: boolean;
  void_juice_ml?: number;
  gold_coins?: number;
  deck_slots?: number;
  email?: string | null;
  email_verified?: boolean;
  unlocked_factions?: string[];
  sleep?: unknown;
};
let cachedUser: MeUser | null = null;
const meListeners = new Set<MeListener>();

export function getCachedMe(): MeUser | null {
  return cachedUser;
}

export function subscribeMe(listener: MeListener): () => void {
  meListeners.add(listener);
  listener(cachedUser);
  return () => {
    meListeners.delete(listener);
  };
}

function setCachedUser(u: MeUser | null): void {
  cachedUser = u;
  for (const l of meListeners) l(u);
}

/** Merge a partial user-state patch from the `user:<id>` channel into
 *  the cached me and notify subscribers. The patch shape mirrors the
 *  `User` payload — recognised keys today are `void_juice_ml` /
 *  `gold_coins` / `sleep` / `unlocked_factions` / `deck_slots`, but
 *  any key the server sends is shallow-merged.
 *
 *  No-op when the cache is empty (the SPA hasn't probed `/api/me`
 *  yet, or the user is logged out) — a fresh patch arriving before
 *  the first probe would synthesize a half-built user.
 *
 *  This is the live-sync entry point that lets a tab see HP and
 *  currency changes driven from elsewhere (other tab, other device,
 *  admin write, engine-internal write). The `useMe` subscribers
 *  re-render in the same frame. */
export function applyMePatch(patch: Partial<MeUser>): void {
  if (!cachedUser) return;
  setCachedUser({ ...cachedUser, ...patch });
}

/** Force-refetch `/api/me` and broadcast to subscribers. Call after
 *  any mutation that might change the user's persistent state. */
export async function refreshMe(): Promise<MeUser | null> {
  try {
    const me = await getMe();
    return me.user;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      setCachedUser(null);
      return null;
    }
    throw err;
  }
}

/**
 * Bootstrap the CSRF token. Safe to call on every app boot; the
 * server treats it as idempotent.
 */
export async function ensureCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  const r = await fetch("/api/csrf", { credentials: "include" });
  const body = (await r.json()) as { csrf_token: string };
  csrfToken = body.csrf_token;
  return csrfToken;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  retried = false,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body) headers["Content-Type"] = "application/json";

  // Every state-changing request needs a CSRF token. If we don't
  // already have one (first POST of the app's lifecycle, or after
  // a refresh where the boot-time bootstrap hasn't landed), block
  // here and fetch one. Phoenix returns 403 if we POST without it.
  if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
    if (!csrfToken) await ensureCsrfToken();
    if (csrfToken) headers["x-csrf-token"] = csrfToken;
  }

  const init: RequestInit = {
    method,
    credentials: "include",
    headers,
    body: body ? JSON.stringify(body) : undefined,
  };
  const r = await fetch(path, init);
  const ct = r.headers.get("content-type") ?? "";
  const parsed = ct.includes("application/json")
    ? await r.json().catch(() => null)
    : null;
  if (!r.ok) {
    // 403 on a state-changing request is most often a stale CSRF
    // token (server cleared the session on logout, or the token
    // rotated). Drop the cached token and retry exactly once with
    // a fresh one. Anything still 403 after the retry is a real
    // authorization failure and falls through to the normal path.
    // Server has banned this user. Fire the registered handler so the
    // SPA can `navigate("/banned")`, then throw so any awaiting code
    // unwinds cleanly. Done before the CSRF-retry branch so a banned
    // response on a POST doesn't bounce through ensureCsrfToken.
    if (
      r.status === 403 &&
      parsed &&
      typeof parsed === "object" &&
      (parsed as { error?: unknown }).error === "banned"
    ) {
      if (bannedHandler) bannedHandler();
      throw new ApiError("banned", 403, parsed);
    }
    // 409 capacity: the lobby cap is full. The server set a
    // `queue_ticket` cookie and returned the queue category +
    // position. Fire the registered handler so App.tsx can navigate
    // to `/full?queue=user|guest`, then throw so the original caller
    // (login form, guest button, …) doesn't proceed.
    if (
      r.status === 409 &&
      parsed &&
      typeof parsed === "object" &&
      (parsed as { error?: unknown }).error === "capacity"
    ) {
      const body = parsed as {
        queue?: unknown;
        ticket?: unknown;
        position?: unknown;
      };
      const queue = body.queue === "guest" ? "guest" : "user";
      const ticket = typeof body.ticket === "string" ? body.ticket : "";
      const position =
        typeof body.position === "number" ? body.position : 0;
      if (capacityHandler) capacityHandler({ queue, ticket, position });
      throw new ApiError("capacity", 409, parsed);
    }
    if (
      r.status === 403 &&
      !retried &&
      method !== "GET" &&
      method !== "HEAD" &&
      method !== "OPTIONS"
    ) {
      csrfToken = null;
      await ensureCsrfToken();
      return request(method, path, body, true);
    }
    let msg = `HTTP ${r.status}`;
    if (parsed && typeof parsed === "object") {
      const obj = parsed as {
        error?: unknown;
        detail?: unknown;
        errors?: unknown;
      };
      const code = obj.error != null ? String(obj.error) : null;
      const detail = obj.detail != null ? formatDetail(obj.detail) : null;
      if (code && detail) msg = `${code}: ${detail}`;
      else if (code) msg = code;
      else if (detail) msg = detail;
      // Phoenix changeset errors land as `{errors: {field: ["msg",
      // ...]}}` from translate_errors/1. Surface them so a 422 says
      // "username: has already been taken" instead of "HTTP 422".
      else if (obj.errors && typeof obj.errors === "object") {
        msg = formatChangesetErrors(obj.errors as Record<string, unknown>);
      }
    }
    throw new ApiError(msg, r.status, parsed);
  }
  // Every successful state-changing request might have mutated the
  // caller's own persistent state (HP, currencies, deck slots,
  // unlocks, items, …). Push a refresh into the me cache so every
  // subscriber (AppHeader currency / HP chips, etc.) reflects the
  // change in the same frame. Fire-and-forget — never blocks the
  // returned promise. Skipped for `/api/me` itself (already refreshes
  // the cache directly) and for `/api/logout` (which clears it).
  if (
    method !== "GET" &&
    method !== "HEAD" &&
    method !== "OPTIONS" &&
    path !== "/api/me" &&
    path !== "/api/logout" &&
    path !== "/api/csrf"
  ) {
    void scheduleMeRefresh();
  }
  return parsed as T;
}

// Debounce live-sync refreshes so a burst of mutations (a multi-step
// cheat, a deck-builder save that fires several writes) collapses
// into one /api/me probe at the end. 30 ms is well under any
// perceived input → repaint round-trip but long enough to merge
// near-simultaneous calls. Kept module-local so callers don't have
// to think about it.
let pendingMeRefresh: ReturnType<typeof setTimeout> | null = null;
function scheduleMeRefresh(): void {
  if (pendingMeRefresh != null) return;
  pendingMeRefresh = setTimeout(() => {
    pendingMeRefresh = null;
    void refreshMe().catch(() => {});
  }, 30);
}

// ---- types shared with Phoenix ----

export interface SleepState {
  /** Live HP at the moment this payload was sent. While
   *  `sleep_started_at` is non-null the client should compute live
   *  HP from `sleep_start_hp + floor((now - sleep_started_at) /
   *  regen_seconds_per_hp)` and cap at `max_hp`. No polling needed. */
  current_hp: number;
  max_hp: number;
  sleeping: boolean;
  /** ISO-8601 string when sleeping; null otherwise. */
  sleep_started_at: string | null;
  sleep_start_hp: number | null;
  regen_seconds_per_hp: number;
}

export interface User {
  id: number;
  username: string;
  is_admin: boolean;
  /** Guests can play matches but cannot open packs or create/edit/delete
   *  decks until they convert via `convertGuest`. */
  is_guest: boolean;
  /** When true, the server has locked this user out of every API. The
   *  SPA hard-routes to `/banned` (see `setBannedHandler` and the 403
   *  interceptor in `request`). On `/api/me` the server returns just
   *  `{id, username, is_banned: true}` and the other fields below
   *  will be missing — guarded by the redirect happening first. */
  is_banned: boolean;
  /** Void Juice balance, in millilitres. Earned from NPC challenges,
   *  spent in shops. */
  void_juice_ml: number;
  /** Gold coins balance. Earned from harder challenges, spent in
   *  shops. */
  gold_coins: number;
  /** Maximum number of *non-starter* decks the user can have. Starts
   *  at 4; bumped by Deck Box item use. */
  deck_slots: number;
  /** The email the user registered with, or null for guests who
   *  haven't converted yet. */
  email: string | null;
  /** True after the user has clicked the verification link in their
   *  signup email. Guests are always false. */
  email_verified: boolean;
  /** Factions the player has currently unlocked — the only factions
   *  any player-facing surface (deck builder, collection filter,
   *  shop, etc.) is allowed to enumerate. Defaults to humans /
   *  goblins / dwarves; orcs and elves appear here only when a
   *  per-user unlock flag is set. */
  unlocked_factions: string[];
  /** Persistent HP + Inn-sleep state. */
  sleep: SleepState;
}

export interface CatalogCard {
  id: string;
  name: string;
  type: "creature" | "spell" | "enchantment" | "trap";
  cost: number;
  attack: number | null;
  life: number | null;
  faction: string;
  /** Creature subtype: Race. Closed enum from data/subtypes.json.
   *  Absent for spells / enchantments / traps and for creatures with
   *  no declared Race. */
  race?: string | null;
  /** Creature subtype: Class. Closed enum from data/subtypes.json. */
  class?: string | null;
  expansion_set: string | null;
  pool: string | null;
  rarity: string;
  rules_text: string | null;
  flavor: string | null;
  copy_cap: number;
  /** Falls back to `/card-images/:id`. Admin sets a per-pool URL. */
  image_url?: string | null;
}

export interface CollectionEntry {
  card_id: string;
  count: number;
  /** ISO-8601 timestamp of the last time the player viewed this card,
   *  or `null` if it's still highlighted as new in the collection. */
  viewed_at: string | null;
}

/** Per-stack rarity returned by the pack roller. The wildcard slot can
 *  be any of these. */
export type PackRarity = "common" | "uncommon" | "rare" | "legendary";

export interface PackCard {
  card_id: string;
  is_new_to_collection: boolean;
}

/** A revealed stack inside an opened pack. Stack length is 1 (singleton
 *  — used by the legendary wildcard) or 2 (a pair of two distinct cards
 *  of the same rarity). Under the unique-per-card pack model each
 *  card_id can only appear once across the entire opening history, so
 *  pairs are now two *different* identities (not two copies of one). */
export interface PackStack {
  rarity: PackRarity;
  cards: PackCard[];
}

export interface PackOpenResponse {
  item_id: string;
  set: string;
  stacks: PackStack[];
  /** Count of this pack item the user still has in inventory after the
   *  open. Drives the "OPEN ANOTHER" affordance on the pack page. */
  remaining_in_inventory: number;
}

/** Threaded through end-of-match flows for backwards compatibility.
 *  Always `null` now — matches don't pay any reward. Currency only
 *  flows from NPC challenges. */
export type ProgressionPayload = null;

/** End-of-match challenge payload — present on challenge battles
 *  (started via NPC challenge endpoint), null for free-play. Carries
 *  the dialogue resume node the SPA hands forward as location.state
 *  when navigating back to the NPC. */
export interface MatchChallengePayload {
  outcome: "win" | "loss";
  npc_id: string;
  set_id: string;
  index: number;
  rewards?: { currency: "void_juice" | "gold"; amount: number }[];
  balances?: { void_juice_ml: number; gold_coins: number };
  already_completed?: boolean;
  /** Author-declared `on_win` / `on_loss` for the entry. Null means
   *  "fall through to the NPC's normal default-node resolution"
   *  (e.g. Pip-style between-round overrides). */
  resume_node: string | null;
}

export interface DeckSummary {
  id: number;
  name: string;
  faction: string;
  /** Starter decks (the five seeded by `Doi.Decks.Starters`) don't
   *  count against the user's `deck_slots` cap. */
  is_starter: boolean;
}

export interface DeckCardEntry {
  card_id: string;
  count: number;
}

export interface Deck {
  id: number;
  name: string;
  faction: string;
  cards: DeckCardEntry[];
}

/** AI seat identity. Nullable fields → generic-label fallback. */
export interface AiPersona {
  faction: string | null;
  name: string | null;
  city: string | null;
}

/** Viewer's seat identity. Nullable fields → generic-label fallback. */
export interface HumanPersona {
  faction: string | null;
  name: string | null;
  deck_name: string | null;
}

/** Tagged deck reference crossing the wire: `"custom:<id>"` for a
 *  player-owned deck row, `"starter:<faction>"` for one of the built-in
 *  starter decks, `"npc:<slug>"` for a promoted NPC deck, `"gen:<slug>"`
 *  for an unpromoted deck-gen deck. */
export type DeckRef =
  | `custom:${number}`
  | `starter:${string}`
  | `npc:${string}`
  | `gen:${string}`;

/** Build a DeckRef from a deck summary. Starter decks live in the
 *  Starters module (faction-keyed); custom decks have positive integer
 *  ids in the `decks` table. */
export function toDeckRef(d: {
  id: number;
  faction: string;
  is_starter: boolean;
}): DeckRef {
  return d.is_starter ? `starter:${d.faction}` : `custom:${d.id}`;
}

export interface GameRow {
  /** Engine session UUID; opaque in URLs / API paths. */
  id: string;
  /** Null on shared-deck PvP rows. */
  deck: DeckRef | null;
  engine_session_id: string;
  ended_at: string | null;
  ai_persona: AiPersona;
}

// ---- auth ----

/** `socket_token` is a short-lived signed token for the multiplayer WS.
 *  `csrf_token` is refreshed on every authed response so the SPA's
 *  copy stays valid for the lifetime of the session.
 *
 *  Both are absent on `/api/me` for banned users — the server returns
 *  a minimal `{user: {id, username, is_banned: true}}` payload so the
 *  SPA can route to `/banned` without exposing a fresh socket token. */
export interface MeResponse {
  user: User;
  socket_token?: string;
  csrf_token?: string;
}

function captureCsrf<T extends { csrf_token?: string }>(resp: T): T {
  if (resp && typeof resp.csrf_token === "string")
    setCsrfToken(resp.csrf_token);
  return resp;
}

// MeResponses from login/loginAsGuest/changePassword/resetPassword
// also carry the canonical user payload — push it into the me cache so
// the AppHeader (subscribed via `useMe`) updates immediately. Cheap
// to wrap every auth call in.
function captureMe<T extends { user?: User }>(resp: T): T {
  if (resp && resp.user) setCachedUser(resp.user as unknown as MeUser);
  return resp;
}

/** Register and Convert no longer return a session. The user has to
 *  verify their email before they can log in. The SPA shows a
 *  "check your email" screen on success. */
export interface VerificationRequiredResponse {
  user: User;
  verification_required: true;
}

export const register = (username: string, email: string, password: string) =>
  request<VerificationRequiredResponse>("POST", "/api/register", {
    username,
    email,
    password,
  });

export const login = (username: string, password: string) =>
  request<MeResponse>("POST", "/api/login", { username, password })
    .then(captureCsrf)
    .then(captureMe);

/** Create a guest account (random name, full starter content) and log
 *  in. The returned user has `is_guest: true`. */
export const loginAsGuest = () =>
  request<MeResponse>("POST", "/api/guest", {})
    .then(captureCsrf)
    .then(captureMe);

/** Convert the current guest into a full account. The server drops
 *  the guest session and requires email verification before login
 *  is allowed — same response shape as `register`. */
export const convertGuest = (
  username: string,
  email: string,
  password: string,
) =>
  request<VerificationRequiredResponse>("POST", "/api/account/convert", {
    username,
    email,
    password,
  });

export const changePassword = (
  current_password: string,
  new_password: string,
) =>
  request<MeResponse>("POST", "/api/account/change-password", {
    current_password,
    new_password,
  })
    .then(captureCsrf)
    .then(captureMe);

/** Always returns `{ok: true}` regardless of whether the email is
 *  registered — that's intentional, so callers can't probe the DB. */
export const forgotPassword = (email: string) =>
  request<{ ok: true }>("POST", "/api/account/forgot-password", { email });

export const resetPassword = (token: string, new_password: string) =>
  request<MeResponse>("POST", "/api/account/reset-password", {
    token,
    new_password,
  })
    .then(captureCsrf)
    .then(captureMe);

/** Verify the email belonging to the link the user clicked. If the
 *  caller is also currently logged in as that user, the server
 *  returns a full MeResponse so the SPA can update its cached
 *  identity. If they aren't, the server returns `{ok: true}` and
 *  the SPA falls back to fetching /api/me. */
export type VerifyEmailResponse = MeResponse | { ok: true };

export const verifyEmail = (token: string) =>
  request<VerifyEmailResponse>("POST", "/api/account/verify-email", {
    token,
  }).then((resp) => {
    if ("user" in resp) {
      captureCsrf(resp);
      captureMe(resp);
    }
    return resp;
  });

/** Public endpoint — takes the email the user is trying to verify.
 *  Always returns 200 regardless of whether the address is in our
 *  DB, so callers can't probe for accounts. Use it from the
 *  "check your email" screen and from the login error path. */
export const resendVerification = (email: string) =>
  request<{ ok: true }>("POST", "/api/account/resend-verification", { email });

export const logout = async () => {
  const res = await request<{ ok: true }>("DELETE", "/api/logout");
  // The server cleared the session, so the cached CSRF token now
  // points at a session that doesn't exist. Drop it so the next
  // state-changing request re-bootstraps. Also clear the cached me
  // so subscribers see the logout immediately.
  setCsrfToken(null);
  setCachedUser(null);
  return res;
};

// Share one in-flight `/api/me` across concurrent callers. A page
// load probes from several layers at once — the socket-token
// bootstrap, RequireAuth's gate, and the page's own data load, each
// doubled by StrictMode in dev — and without dedupe every one of
// them pays a full server round-trip. Only *concurrent* calls are
// merged; a call issued after the previous one settled still hits
// the network, so `refreshMe()` keeps forcing fresh data after
// mutations.
let inflightMe: Promise<MeResponse> | null = null;

export const getMe = (): Promise<MeResponse> => {
  if (inflightMe) return inflightMe;
  const p = request<MeResponse>("GET", "/api/me")
    .then(captureCsrf)
    .then((me) => {
      setCachedUser(me.user as unknown as MeUser);
      return me;
    })
    .catch((err) => {
      // 401 means the session is gone. Clear the cache here (not
      // just in refreshMe) so every subscriber — header chips,
      // RequireAuth's stale-while-revalidate paint — sees the
      // logout no matter which caller's probe discovered it.
      if (err instanceof ApiError && err.status === 401) {
        setCachedUser(null);
      }
      throw err;
    })
    .finally(() => {
      if (inflightMe === p) inflightMe = null;
    });
  inflightMe = p;
  return p;
};

// ---- Lobby queue (server-full path) ----

export type QueueStatusResponse =
  | { ready: true }
  | { ready: false; position: number };

/** Polled by the /full page. The ticket lives in a server-set cookie
 *  (`queue_ticket`), so the call carries no payload. A `ready: true`
 *  reply means the SPA should redirect to /login and retry; the next
 *  /api/login attempt with that cookie admits the holder. */
export const queueStatus = () =>
  request<QueueStatusResponse>("GET", "/api/queue/status");

// ---- Inn / sleep ----

export const innSleep = () =>
  request<{ sleep: SleepState }>("POST", "/api/inn/sleep");

export const innWake = () =>
  request<{ sleep: SleepState }>("POST", "/api/inn/wake");

/** Closed-form live HP for a sleep snapshot. Returns the same answer
 *  the server's `Doi.Accounts.effective_hp/2` would, given `now`.
 *  Use this in setInterval-driven render loops; no polling. */
export function liveHp(sleep: SleepState, now: number = Date.now()): number {
  if (
    !sleep.sleeping ||
    !sleep.sleep_started_at ||
    sleep.sleep_start_hp == null
  ) {
    return sleep.current_hp;
  }
  const startedMs = Date.parse(sleep.sleep_started_at);
  const elapsedSec = Math.max(0, (now - startedMs) / 1000);
  const gained = Math.floor(elapsedSec / sleep.regen_seconds_per_hp);
  return Math.min(sleep.max_hp, sleep.sleep_start_hp + gained);
}

// ---- catalog / collection / decks ----

// Every card must carry a faction (Neutral was removed 04/2026).
function normalizeCatalog(cards: CatalogCard[]): CatalogCard[] {
  return cards.map((c) => {
    const raw = c.faction as string | null | undefined;
    if (!raw || raw.length === 0) {
      throw new Error(`catalog card ${c.id} has no faction`);
    }
    return c;
  });
}

// Public, anon-reachable. Returns a shuffled sample of base_set
// cards with art, used by the landing page marquee. Capped server-side.
// `include` is a list of card ids that should always appear in the
// response (the landing page pins its hero cards this way).
export const getLandingSampleCards = async (
  n: number = 24,
  include: string[] = [],
) => {
  const qs = new URLSearchParams({ n: String(n) });
  if (include.length > 0) qs.set("include", include.join(","));
  const { cards } = await request<{ cards: CatalogCard[] }>(
    "GET",
    `/api/landing/sample-cards?${qs}`,
  );
  return { cards: normalizeCatalog(cards) };
};

// Admin-only. Player-facing pages use getOwnedCatalog.
export const getCatalog = async () => {
  const { cards } = await request<{ cards: CatalogCard[] }>(
    "GET",
    "/api/catalog",
  );
  return { cards: normalizeCatalog(cards) };
};

// Same shape as /api/catalog but only the cards the user owns.
export const getOwnedCatalog = async () => {
  const { cards } = await request<{ cards: CatalogCard[] }>(
    "GET",
    "/api/catalog/owned",
  );
  return { cards: normalizeCatalog(cards) };
};

export interface CardListFilters {
  q?: string;
  factions?: string[];
  type?: string;
}

function appendCardListFilters(qs: URLSearchParams, filters?: CardListFilters) {
  const q = filters?.q?.trim();
  if (q) qs.set("q", q);
  if (filters?.type && filters.type !== "all") qs.set("type", filters.type);
  if (filters?.factions && filters.factions.length > 0) {
    qs.set("factions", filters.factions.join(","));
  }
}

export const getOwnedCatalogPage = async (
  page: number,
  perPage = 50,
  filters?: CardListFilters,
) => {
  const qs = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });
  appendCardListFilters(qs, filters);
  const { cards, total } = await request<{
    cards: CatalogCard[];
    total: number;
  }>("GET", `/api/catalog/owned?${qs}`);
  return { cards: normalizeCatalog(cards), total };
};

// Used by the battle log to inspect opponent-played cards the
// viewer doesn't own.
export const getCardById = async (id: string) => {
  const { card } = await request<{ card: CatalogCard }>(
    "GET",
    `/api/catalog/by-id/${encodeURIComponent(id)}`,
  );
  return normalizeCatalog([card])[0];
};

// Admin-only variant of `getCardById` that bypasses the
// encountered-by-this-user visibility gate. Use this for admin UIs
// surfacing arbitrary card_ids the admin may never have personally
// played against (e.g. cards in an evolved tournament deck).
export const getAdminCardCatalog = async (id: string) => {
  const { card } = await request<{ card: CatalogCard }>(
    "GET",
    `/api/admin/cards/by-id/${encodeURIComponent(id)}`,
  );
  return normalizeCatalog([card])[0];
};

export const getCollection = () =>
  request<{ entries: CollectionEntry[] }>("GET", "/api/collection");

export const getCollectionPage = (
  page: number,
  perPage = 50,
  filters?: CardListFilters,
) => {
  const qs = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });
  appendCardListFilters(qs, filters);
  return request<{ entries: CollectionEntry[]; total: number }>(
    "GET",
    `/api/collection?${qs}`,
  );
};

export const listDecks = () =>
  request<{ decks: DeckSummary[] }>("GET", "/api/decks");

export const getDeck = (id: number | string) =>
  request<{ deck: Deck }>("GET", `/api/decks/${id}`);

export const createDeck = (input: {
  name: string;
  faction: string;
  cards: Array<[string, number]>;
}) => request<{ deck: Deck }>("POST", "/api/decks", { deck: input });

export const updateDeck = (
  id: number | string,
  input: { name: string; faction: string; cards: Array<[string, number]> },
) => request<{ deck: Deck }>("PUT", `/api/decks/${id}`, { deck: input });

export const deleteDeck = (id: number | string) =>
  request<void>("DELETE", `/api/decks/${id}`);

// ---- games ----

export const getActiveGame = () =>
  request<{ active: GameRow | null }>("GET", "/api/games");

export interface SeatPersona {
  name: string | null;
  faction: string | null;
  deck_name: string | null;
}

/** Tentative-combat preview the SPA sends to the v2 `/state` endpoint
 *  during attackers / blocks selection. The engine treats these eids
 *  as if they were the declared attackers / pairs so continuous
 *  query-layer keywords (Rally, Reinforce) reflect the would-be buff
 *  in `current_attack` / `remaining_life`. */
export interface PreviewState {
  /** Eids the active player has tentatively marked as attackers. */
  preview_attackers?: number[];
  /** Tentative (blocker, attacker) pairs from the blocks-phase picker. */
  preview_blocks?: Array<[number, number]>;
  /** Spectator perspective override. Honoured server-side only when
   *  the requester is a spectator (not seated). */
  viewer_seat?: 0 | 1;
}

function previewToQuery(preview?: PreviewState): Record<string, string> {
  const q: Record<string, string> = {};
  if (preview?.preview_attackers?.length) {
    q.preview_attackers = preview.preview_attackers.join(",");
  }
  if (preview?.preview_blocks?.length) {
    q.preview_blocks = preview.preview_blocks
      .map(([b, a]) => `${b}:${a}`)
      .join(",");
  }
  if (preview?.viewer_seat === 0 || preview?.viewer_seat === 1) {
    q.viewer_seat = String(preview.viewer_seat);
  }
  return q;
}

function appendQuery(path: string, q: Record<string, string>): string {
  const entries = Object.entries(q);
  if (entries.length === 0) return path;
  const qs = entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
  return path.includes("?") ? `${path}&${qs}` : `${path}?${qs}`;
}

/** v2 wire-format per-seat scoped `MatchSnapshot` plus the Phoenix
 *  wrapper fields (`seat`, personas, `seat_personas`,
 *  `opponent_connected`, …) so BattlePage can keep its perspective /
 *  persona UI without splitting fetches. Used on mount, on reconnect,
 *  and as the gap-detect resync target whenever a channel `events`
 *  push lands with `first_seq != lastEventSeq + 1`. `preview` accepts
 *  tentative attackers / blocks for Rally/Reinforce stat preview and a
 *  `viewer_seat` spectator perspective override. */
export const getGameStateV2 = (
  gameId: number | string,
  preview?: PreviewState,
) =>
  request<
    MatchSnapshot & {
      ai_persona?: AiPersona;
      human_persona?: HumanPersona;
      /** Engine seat the snapshot was built for (0 or 1). For players
       *  this is their own seat; for spectators it's the seat they
       *  asked for via `viewer_seat`. */
      seat?: 0 | 1;
      /** Multiplayer room id this game belongs to, or null for
       *  single-player. When present, the BattlePage routes back to
       *  `/room/<id>` after the match instead of `/lobby`. */
      room_id?: string | null;
      /** Seconds since the engine last accepted an action. Drives the
       *  PvP claim-victory affordance. */
      idle_seconds?: number;
      /** True iff the opponent's BattlePage has an active WebSocket
       *  channel subscription. */
      opponent_connected?: boolean;
      /** True when the requesting user is a spectator (not seated). */
      spectating?: boolean;
      /** Both seats' personas — present whenever the snapshot is built
       *  so spectators can render "Watching <A> vs <B>" perspective
       *  toggles without having to ask `Doi.Match.info` separately. */
      seat_personas?: Record<"0" | "1", SeatPersona>;
    }
  >("GET", appendQuery(`/api/games/${gameId}/state`, previewToQuery(preview)));

/** v2 wire-format action submission. Returns the compact
 *  `ActionResponse` (`viewer_events`, `p0_life`, `p1_life`, `result`);
 *  `opponent_events` are routed over the `game:<sid>` channel and
 *  never decoded by Phoenix. */
export const submitGameActionV2 = (
  gameId: number | string,
  action: ActionBody,
) =>
  request<ActionResponse & { stale?: boolean }>(
    "POST",
    `/api/games/${gameId}/action`,
    action,
  );

export const endGame = (gameId: number | string) =>
  request<{
    game: GameRow;
    progression: ProgressionPayload | null;
    challenge: MatchChallengePayload | null;
  }>("DELETE", `/api/games/${gameId}`);

/** PvP idle-victory. Same shape as endGame; 409 if conditions unmet. */
export const claimVictory = (gameId: number | string) =>
  request<{
    game: GameRow;
    progression: ProgressionPayload | null;
    challenge: MatchChallengePayload | null;
  }>("POST", `/api/games/${gameId}/claim-victory`, {});

// ---- rooms ----

export const createRoom = () =>
  request<{ room_id: string }>("POST", "/api/rooms", {});

// ---- packs ----

// ---- world tree ----

export interface WorldGate {
  /** Always `true` on the wire — server only emits this object when locked. */
  locked: true;
  /** Optional human-readable hint authored on the gate's `hint:` field. */
  hint: string | null;
}

export interface WorldChild {
  id: string;
  kind: "nav" | "shop" | "npc" | "forage" | "cook" | "arena";
  path: string;
  title: string;
  description: string | null;
  button_label: string | null;
  /** Null when the child is reachable; otherwise the locked object. */
  gate: WorldGate | null;
  /** For `kind: "npc"` children only: the referenced NPC's id and
   *  default mood (uppercase, e.g. "OFFICIOUS"). The frontend uses
   *  these to fall back to `<npc_ref>_<DEFAULT_MOOD>.png` when no
   *  location-style portrait at `<id>.png` exists. */
  npc_ref?: string | null;
  default_mood?: string | null;
}

export interface WorldShopEntry {
  item_id: string;
  cost: { currency: "void_juice" | "gold"; amount: number };
  stock: { kind: "daily" | "lifetime" | "infinite"; max: number | null };
}

export interface WorldShopBlock {
  shop_id: string;
  npc: {
    name: string | null;
    role: string | null;
    faction: string | null;
    greeting: string | null;
  };
  entries: WorldShopEntry[];
}

export type WorldNode =
  | {
      id: string;
      kind: "nav";
      path: string;
      title: string;
      breadcrumb: string | null;
      description: string | null;
      button_label: string | null;
      children: WorldChild[];
    }
  | {
      id: string;
      kind: "shop";
      path: string;
      title: string;
      breadcrumb: string | null;
      description: string | null;
      button_label: string | null;
      shop: WorldShopBlock;
    }
  | {
      id: string;
      kind: "npc";
      path: string;
      title: string;
      breadcrumb: string | null;
      description: string | null;
      button_label: string | null;
      npc_ref: string;
    }
  | {
      id: string;
      kind: "forage";
      path: string;
      title: string;
      breadcrumb: string | null;
      description: string | null;
      button_label: string | null;
      forage_kind: "apple_trees" | "strawberry_bushes";
    }
  | {
      id: string;
      kind: "cook";
      path: string;
      title: string;
      breadcrumb: string | null;
      description: string | null;
      button_label: string | null;
      cook_kind: "kitchens";
    }
  | {
      id: string;
      kind: "arena";
      path: string;
      title: string;
      breadcrumb: string | null;
      description: string | null;
      button_label: string | null;
    };

export type WorldResponse = { node: WorldNode };

export const getWorldNode = (path?: string) => {
  const qs = path ? `?path=${encodeURIComponent(path)}` : "";
  return request<WorldResponse>("GET", `/api/world${qs}`);
};

// ---- NPCs & challenges ----

export interface NpcSpeaker {
  id: string;
  name: string;
  faction: string | null;
  role: string | null;
  portrait_id: string;
  default_mood: string | null;
  /** True for the page's primary speaker (npc.id === speaker.id). The
   *  frontend uses this to render non-primary speakers' portraits on
   *  the right rather than the left. */
  is_primary: boolean;
}

export interface NpcPayload {
  id: string;
  name: string;
  faction: string;
  role: string | null;
  default_image: string | null;
  default_mood: string | null;
  /** Map of speaker id → speaker block, including the primary. Authors
   *  declare additional speakers under YAML `speakers:`; the primary is
   *  synthesized from the NPC's top-level fields under its own id. */
  speakers: Record<string, NpcSpeaker>;
  /** Every flag id this NPC can write — both dialogue `set_flags:`
   *  on basic options AND challenge-entry reward flags. The admin
   *  flag-toggle panel uses this to know what checkboxes to draw. */
  flag_ids: string[];
}

/** Pure-navigation option on a basic node. Picking it navigates to
 *  `goto`'s node; that node's `text` / `speaker` / `mood` is what
 *  plays on arrival. Side effects (set_flags / grant / once) fire on
 *  the transition itself via a server roundtrip when `has_side_effects`
 *  is true. */
export interface NpcDialogueOption {
  kind: "basic" | "leave" | "challenge";
  label: string;
  goto?: string;
  /** Server-side flag writes / grant rewards / once-recording need a
   *  roundtrip via `pickNpcOption`. */
  has_side_effects?: boolean;
  /** Catalog index of this option in the node's full options list.
   *  Round-trip with this rather than the in-array position — the
   *  rendered list is filtered by visible_when / once-taken, so
   *  positions shift but catalog indices are stable. */
  index?: number;
}

/** Hub branch — pure-navigation button on a hub. Picking it enters the
 *  reply node named by `goto`; that node owns the response text and
 *  speaker. The frontend tracks the originating hub so a reply node
 *  with no options of its own renders a synthetic "Continue" button
 *  back to the hub. `once: true` branches are persisted as "taken"
 *  and filtered out on subsequent renders. */
export type NpcHubBranch =
  | {
      kind: "leave";
      label: string;
      index: number;
      has_side_effects?: boolean;
    }
  | {
      kind: "branch";
      label: string;
      once: boolean;
      goto: string;
      /** Server-side flag writes / grant rewards / once-recording. */
      has_side_effects?: boolean;
      index: number;
    };

export interface NpcDialogueNode {
  id: string;
  /** `"hub"` for hub-and-spoke nodes (branches stay in place); the
   *  default `"basic"` covers everything else. */
  kind: "basic" | "hub";
  mood: string | null;
  text: string;
  speaker: NpcSpeaker | null;
  /** Names the challenge set whose stepper renders below this node's
   *  dialogue. `null` (the default) means no panel. */
  challenge_set_id: string | null;
  /** Hub branches — in-place responses. Empty on non-hub nodes. */
  branches: NpcHubBranch[];
  /** Cross-node options — transitions away from this node. A hub may
   *  also carry options (e.g. the clerk asks repeatable questions
   *  *and* offers "Demand to see the ambassador" which gotos
   *  elsewhere). */
  options: NpcDialogueOption[];
}

export type NpcChallengeReward =
  | { kind: "currency"; currency: "void_juice" | "gold"; amount: number }
  | { kind: "flag"; flag: string; value: number; label: string }
  | {
      kind: "item";
      item_id: string;
      count: number;
      name: string;
      sprite: string | null;
    };

export type NpcChallengeState = "available" | "locked" | "completed";

export interface NpcChallengeEntry {
  index: number;
  title: string;
  /** Speaker presenting the challenge in the fight modal. Defaults
   *  to the NPC's primary speaker when the YAML doesn't override. */
  speaker: NpcSpeaker | null;
  constraint:
    | { kind: "faction"; factions: string[] }
    | { kind: "non_faction"; factions: string[] }
    | null;
  rewards: NpcChallengeReward[];
  /** Where the dialogue resumes after the player wins or loses this
   *  challenge. Optional node ids in the same NPC's dialogue tree;
   *  null = "fall through to the NPC's normal default-node
   *  resolution" (the usual case — between-round dialogue lives in
   *  the NPC's overrides, not on the entry). */
  on_win: string | null;
  on_loss: string | null;
  state: NpcChallengeState;
}

export interface NpcChallengeSet {
  set_id: string;
  deck_slug: string;
  completed_today: number;
  entries: NpcChallengeEntry[];
}

/** What a dialogue-option `grant:` actually paid out. Items first
 *  (anchored by sprite + name in the modal), then currencies. Only
 *  present on `pickNpcOption` responses where the option had a
 *  non-empty `grant:` block. */
export type NpcGrantedEntry =
  | {
      kind: "item";
      item_id: string;
      count: number;
      name: string;
      sprite: string | null;
    }
  | { kind: "currency"; currency: "void_juice" | "gold"; amount: number };

export interface NpcResponse {
  npc: NpcPayload;
  dialogue: {
    current_node: NpcDialogueNode;
    nodes: Record<string, NpcDialogueNode>;
  };
  /** Every challenge set this NPC declares (keyed by set_id). The
   *  current dialogue node's `challenge_set_id` tells the SPA which
   *  set to render below the dialogue. An NPC can declare zero, one,
   *  or several sets — the Embassy clerk page hosts the Tordrin
   *  ambassador set without the clerk's intro nodes themselves
   *  surfacing the panel. */
  challenge_sets: Record<string, NpcChallengeSet>;
  /** Per-flag current value for every flag in `npc.flag_ids`. Used by
   *  the admin flag-toggle panel; null = unset, any integer = set to
   *  that value. Always populated; the SPA gates rendering on admin
   *  mode rather than the server gating the payload. */
  flag_state: Record<string, number | null>;
  resets_in_seconds: number;
  /** Populated only by `pickNpcOption` responses where the option
   *  carried a non-empty `grant:` block. The frontend pops a rewards
   *  modal listing these in order. */
  granted?: NpcGrantedEntry[];
}

export const getNpc = (npcId: string) =>
  request<NpcResponse>("GET", `/api/npcs/${encodeURIComponent(npcId)}`);

export const pickNpcOption = (
  npcId: string,
  nodeId: string,
  optionIndex: number,
) =>
  request<NpcResponse>(
    "POST",
    `/api/npcs/${encodeURIComponent(npcId)}/dialogue/${encodeURIComponent(nodeId)}/options/${optionIndex}`,
  );

export interface ChallengeStartResponse {
  engine_session_id: string;
  game_id: string;
}

export const startChallenge = (
  npcId: string,
  setId: string,
  index: number,
  deck: DeckRef,
) =>
  request<ChallengeStartResponse>(
    "POST",
    `/api/challenges/${encodeURIComponent(npcId)}/${encodeURIComponent(setId)}/${index}/start`,
    { deck },
  );

// ---- arena ----

export type ArenaTier = "easy" | "medium" | "hard" | "boss";
export type ArenaRunStatus = "active" | "cleared" | "lost" | "abandoned";
export type ArenaClaimKind = "cleared" | "abandoned" | "lost";

export interface ArenaOpponent {
  round: number;
  tier: ArenaTier;
  npc_id: string;
  deck_slug: string;
  /** Display name from the project-level speaker catalog. Falls back
   *  to the humanized npc_id when the speaker yaml is missing. */
  name: string | null;
  /** Faction key from the speaker catalog (one of the five engine
   *  factions). Drives the opponent portrait's tint and corner sigil. */
  faction: string | null;
  /** Default mood from the speaker catalog — used to resolve the
   *  `/portraits/npcs/<id>_<MOOD>.png` URL on the opponent panel. */
  default_mood: string | null;
}

export interface ArenaHeldItem {
  item_id: string;
  count: number;
}

export interface ArenaRun {
  id: number;
  status: ArenaRunStatus;
  current_round: number;
  wins_count: number;
  locked_deck_id: number | null;
  locked_deck_ref: DeckRef | null;
  current_opponent: ArenaOpponent | null;
  next_opponent: ArenaOpponent | null;
  held_items: ArenaHeldItem[];
  enrolled_at: string | null;
  ended_at: string | null;
}

export interface ArenaRewardSlot {
  round: number;
  tier: ArenaTier;
  currencies: { currency: "void_juice" | "gold"; amount: number }[];
  items: ArenaHeldItem[];
}

export interface ArenaState {
  run: ArenaRun | null;
  held: { items: ArenaHeldItem[]; run_id: number | null };
  rewards_table: ArenaRewardSlot[];
  /** How many `arena_ticket` items the user is currently holding.
   *  The bracket renders `ENROL (N ARENA TICKET[S])` and disables
   *  the button at 0 so the player has to detour to the clerk. */
  ticket_count: number;
}

export interface ArenaClaimResponse {
  granted: {
    currencies: { currency: string; amount: number }[];
    items: ArenaHeldItem[];
    held: ArenaHeldItem[];
  };
  balances: { void_juice_ml: number; gold_coins: number };
  run: ArenaRun;
  arena: ArenaState;
}

export const getArenaState = () =>
  request<{ arena: ArenaState }>("GET", "/api/arena/state");

export const enrolInArena = () =>
  request<{ run: ArenaRun; arena: ArenaState }>("POST", "/api/arena/enrol");

export const startArenaRound = (deck: DeckRef) =>
  request<{ engine_session_id: string; game_id: string }>(
    "POST",
    "/api/arena/round/start",
    { deck },
  );

export const claimArena = (kind: ArenaClaimKind) =>
  request<ArenaClaimResponse>("POST", "/api/arena/claim", { kind });

export const collectArenaHeld = () =>
  request<{ arena: ArenaState }>("POST", "/api/arena/collect");

// ---- shops ----

export interface ShopEntryState {
  item_id: string;
  cost: { currency: "void_juice" | "gold"; amount: number };
  /** `kind: "infinite"` items have `max: null` and `remaining: null`
   *  — treat as never out of stock. */
  stock: { kind: "daily" | "lifetime" | "infinite"; max: number | null };
  used: number;
  remaining: number | null;
}

export interface ShopCardEntry {
  slot_index: number;
  rarity: "rare" | "legendary" | "common" | "uncommon";
  copies: number;
  cost: { currency: "void_juice" | "gold"; amount: number };
  card_id: string | null;
  sold: boolean;
  out_of_stock: boolean;
}

export interface ShopState {
  shop_id: string;
  entries: ShopEntryState[];
  card_entries: ShopCardEntry[];
  resets_in_seconds: number;
}

export const getShop = (shopId: string) =>
  request<ShopState>("GET", `/api/shops/${encodeURIComponent(shopId)}`);

export interface BuyResponse {
  slots: InventorySlot[];
  balances: { void_juice_ml: number; gold_coins: number };
  entries: ShopEntryState[];
}

export const buyFromShop = (shopId: string, itemId: string) =>
  request<BuyResponse>("POST", `/api/shops/${encodeURIComponent(shopId)}/buy`, {
    item_id: itemId,
  });

export interface BuyCardResponse {
  balances: { void_juice_ml: number; gold_coins: number };
  card_entries: ShopCardEntry[];
  card_id: string;
  copies: number;
}

export const buyCardFromShop = async (shopId: string, slotIndex: number) => {
  const resp = await request<BuyCardResponse>(
    "POST",
    `/api/shops/${encodeURIComponent(shopId)}/buy_card`,
    { slot_index: slotIndex },
  );
  invalidateCatalog();
  return resp;
};

// ---- inventory & items ----

export interface InventorySlot {
  slot_index: number;
  item_id: string | null;
  count: number;
}

export type ItemUseKind =
  | "consume"
  | "rip_open_pack"
  | "add_deck_slot"
  | "heal"
  | "grant_bundle";

/** What a `grant_bundle` item paid out when opened — surfaced to the
 *  player in a rewards popup. Mirrors the server's `extra.granted`. */
export interface BundleGranted {
  gold: number;
  void_juice: number;
  items: { item_id: string; count: number }[];
  unlocked_factions: string[];
}

export interface ItemDefinition {
  id: string;
  name: string;
  display_category: string;
  description: string;
  flavor: string | null;
  sprite: string;
  stack_max: number;
  use: {
    label: string;
    kind: ItemUseKind;
    /** Only set on `kind: "heal"` items — how much HP the use
     *  restores. Surfaced to the player below the description. */
    health_restore?: number;
    /** Only set on `kind: "rip_open_pack"` items — the card set
     *  the pack rolls from. */
    set?: string | null;
    /** Only set on `kind: "rip_open_pack"` items — the faction the
     *  pack is restricted to (null for set-wide packs). Drives the
     *  pack-opening page color scheme. */
    faction?: string | null;
  } | null;
}

export interface InventoryResponse {
  slots: InventorySlot[];
  items: ItemDefinition[];
  capacity: number;
}

export const getInventory = () =>
  request<InventoryResponse>("GET", "/api/inventory");

export interface UseItemResponse {
  slots: InventorySlot[];
  use_kind: ItemUseKind;
  extra: Record<string, unknown> | null;
}

export const useInventorySlot = (slotIndex: number) =>
  request<UseItemResponse>("POST", `/api/inventory/${slotIndex}/use`, {});

export const dropInventorySlot = (slotIndex: number, count = 1) =>
  request<{ slots: InventorySlot[] }>(
    "POST",
    `/api/inventory/${slotIndex}/drop`,
    { count },
  );

export const openPack = async (itemId: string) => {
  const resp = await request<PackOpenResponse>("POST", "/api/packs/open", {
    item_id: itemId,
  });
  invalidateCatalog();
  return resp;
};

export const markCollectionSeen = (cardIds: string[]) =>
  request<{ updated: number }>("POST", "/api/collection/seen", {
    card_ids: cardIds,
  });

// ---- admin ----

// Just set names (`base_set` + whatever set directories exist under
// cards/). String so new sets work without a frontend deploy.
export type AdminPool = string;

export interface AdminCardInfo {
  pool: AdminPool;
  id: string;
  name: string | null;
  type: string | null;
  cost: number | null;
  attack: number | null;
  life: number | null;
  faction: string | null;
  /** Creature subtype: Race. Closed enum from subtypes.json. */
  race?: string | null;
  /** Creature subtype: Class. */
  class?: string | null;
  expansion_set: string | null;
  rarity: string | null;
  rules_text: string | null;
  flavor: string | null;
  created_at: string | null;
  image_url: string | false | null;
  has_script: boolean;
  has_image: boolean;
  /** Null for unclaimed and for base-set cards (always shared). */
  claimed_by_user_id: number | null;
}

export interface AdminCardDetail {
  pool: AdminPool;
  id: string;
  json_text: string;
  lua_text: string | null;
  has_image: boolean;
}

export type AdminJobKind =
  // Each logical card generation now spans two server-side jobs so
  // the OpenRouter and PixelLab provider caps can be tuned
  // independently. The text job auto-enqueues an image job per
  // saved card.
  | "generate_card_text"
  | "generate_card_image"
  | "generate_deck"
  | "generate_deck_images"
  | "regen_image"
  | "generate_image_grid"
  | "card_power_sweep"
  | "deck_power_sweep"
  | "compare_models"
  | "compare_prompts"
  | "validator_eval";
export type AdminJobStatus = "queued" | "running" | "succeeded" | "failed";

export interface AdminJobStageTimings {
  card_id: string;
  llm: number;
  validation: number;
  image: number;
  balance: number;
  total: number;
  /** Typicality score the generator sampled (or pinned via flag) for
   *  this card, 1–10. `null` for legacy job output that pre-dates the
   *  field — render with a neutral fallback. */
  typicality: number | null;
  /** Seed words the generator drew for this card. Empty for legacy
   *  job output that pre-dates the field. */
  seed_words: string[];
}

/** Per-card LLM token / prompt-cache totals reported by the
 *  Claude-Agent-SDK on the generation path. Summed across every retry
 *  attempt the card needed. `cache_read` near the total of the four
 *  fields means the system prompt is hitting cache; `cache_write`
 *  dominates on cold runs that paid the prefix once. */
export interface AdminJobUsage {
  card_id: string;
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens: number;
  cache_creation_input_tokens: number;
}

/** Per-card USD cost estimate, computed server-side from the same
 *  usage row using `Doi.Admin.Pricing` rates. `total_usd` is `null`
 *  only when both LLM and image components are unknown; if just one
 *  component is missing it contributes 0 to the total. */
export interface AdminJobCardCost {
  card_id: string;
  model: string | null;
  llm_usd: number | null;
  validator_model: string | null;
  validation_usd: number | null;
  image_provider: string | null;
  image_usd: number | null;
  total_usd: number | null;
}

export interface AdminJobResult {
  saved_ids?: string[];
  timings?: AdminJobStageTimings[];
  usage?: AdminJobUsage[];
  /** Per-card cost breakdown (LLM + image USD). One entry per usage
   *  row. Older job results that pre-date cost tracking won't include
   *  this — render token totals only in that case. */
  costs?: AdminJobCardCost[];
  /** Anthropic model id the generate job used (default
   *  `claude-sonnet-4-6`). Surfaced so the cost line can label which
   *  rate card the USD figure was computed from. */
  model?: string;
  /** Image provider key — currently always `pixellab_pixflux_128`. */
  image_provider?: string;
  output?: string;
  /** Image Creator (`generate_image_grid`): how many variations the
   *  run produced. The bytes come from `imageCreatorImageUrl`, indexed
   *  `0..count-1`. */
  count?: number;
  /** Image Creator: square pixel size each variation was generated at. */
  size?: number;
  /** Image Creator: the prompt the run was generated from. */
  description?: string;
  /** Image Creator: whether the background was removed (transparent). */
  no_background?: boolean;
}

export interface AdminJob {
  id: string;
  kind: AdminJobKind;
  status: AdminJobStatus;
  args: Record<string, unknown>;
  inserted_at: string;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
  result: AdminJobResult | null;
}

export interface AdminCardListParams {
  page?: number;
  per_page?: number;
  pool?: AdminPool | "all";
  claim?: "all" | "unclaimed";
  sort?: "cost" | "created";
  q?: string;
  factions?: string;
  type?: string;
}

export const listAdminCards = (params: AdminCardListParams = {}) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<{
    cards: AdminCardInfo[];
    pools: string[];
    total: number;
    page: number;
    per_page: number;
  }>("GET", `/api/admin/cards${suffix}`);
};

export const getAdminCard = (id: string) =>
  request<AdminCardDetail>("GET", `/api/admin/cards/${id}`);

export const saveAdminCard = (
  id: string,
  payload: { json: string; lua: string | null },
) => request<void>("PUT", `/api/admin/cards/${id}`, payload);

export const enqueueRegenImage = (id: string) =>
  request<{ job_id: string }>("POST", `/api/admin/cards/${id}/regen-image`);

// ---- admin: Image Creator tool ----

export interface EnqueueImageCreatorInput {
  description: string;
  /** Square pixel size. Also decides how many images come back:
   *  <=42 -> 64, 43-85 -> 16, 86-170 -> 4, >170 -> 1. */
  size: number;
  /** Remove the background (transparent PNGs). Defaults to keeping it. */
  no_background?: boolean;
}

/** Enqueue one Image Creator run (a `generate_image_grid` job). The
 *  generated variations are read back via `imageCreatorImageUrl`. */
export const enqueueImageCreator = (input: EnqueueImageCreatorInput) =>
  request<{ job_id: string }>("POST", "/api/admin/tools/image-creator", input);

/** URL of one snapped variation from a finished Image Creator run.
 *  `idx` is 0-based and must be `< result.count`. */
export const imageCreatorImageUrl = (jobId: string, idx: number): string =>
  `/api/admin/tools/image-creator/${encodeURIComponent(jobId)}/${idx}`;

export interface AdminPortrait {
  /** PNG filename inside `<portraits_dir>/npcs/`, e.g.
   *  `tordrin_bronzebones_STOIC.png` or `cardmaster.png`. */
  filename: string;
  /** Filename stem with the optional `_<MOOD>` suffix stripped. */
  base: string;
  /** All-caps mood (`STOIC`, `WARY`, …) or `null` for bare-name files. */
  mood: string | null;
}

export const listAdminPortraits = () =>
  request<{ portraits: AdminPortrait[] }>("GET", "/api/admin/portraits");

/** Copy `<portraits_dir>/npcs/<filename>` over the card's `card.png`. */
export const useAdminPortrait = (id: string, filename: string) =>
  request<void>("POST", `/api/admin/cards/${id}/use-portrait`, { filename });

export const grantToAll = (id: string) =>
  request<void>("POST", `/api/admin/cards/${id}/grant-all`);

export const createAdminCard = (payload: {
  pool: AdminPool;
  id: string;
  json: string;
  lua: string | null;
  faction?: string;
}) =>
  request<{ pool: AdminPool; id: string }>("POST", "/api/admin/cards", payload);

export const moveAdminCard = (
  id: string,
  payload: { new_pool: string; faction?: string },
) =>
  request<{ pool: AdminPool; id: string }>(
    "POST",
    `/api/admin/cards/${id}/move`,
    payload,
  );

export interface DeleteAdminCardResponse {
  /** How many orphan rows the cascade cleaned up across each table. */
  counts: {
    claims: number;
    collections: number;
    deck_cards: number;
    shop_rotations: number;
  };
}

export const deleteAdminCard = (id: string) =>
  request<DeleteAdminCardResponse>("DELETE", `/api/admin/cards/${id}`);

export interface CardEditorPrefilled {
  card_json: Record<string, unknown>;
  script: string | null;
  /** Spec-axis overrides for the generator's sampler. Only keys
   *  the admin has explicitly chosen appear; missing keys fall back
   *  to random sampling. (`faction` / `rarity` / `cost` / `card_type`
   *  are still derived from `card_json`.) */
  spec?: {
    seed_words?: string[];
    typicality?: number;
    /** Free-form admin notes routed into the user prompt verbatim.
     *  Used for instructions the structured axes can't express. */
    notes?: string;
  };
}

export const enqueueFillCard = (prefilled: CardEditorPrefilled) =>
  request<{ job_id: string }>("POST", "/api/admin/cards/fill", { prefilled });

// ===== Build-deck-with-ai =====

export interface SlotBrief {
  cost: number;
  card_type: "creature" | "spell" | "enchantment" | "trap" | null;
  rarity: "common" | "uncommon" | "rare" | "legendary" | null;
  count: number;
  notes: string;
}

export interface SlotCandidate {
  card_id: string;
  generated_at: string;
}

export interface DeckSlot {
  idx: number;
  brief: SlotBrief;
  candidates: SlotCandidate[];
  chosen: string | null;
}

export interface DeckState {
  slug: string;
  faction: string;
  deck_size: number;
  copy_cap: number;
  name: string;
  gameplan_blurb: string;
  slots: DeckSlot[];
}

export const enqueueArchitectDeck = (args: {
  slug: string;
  faction: string;
  theme: string;
  deck_size?: number;
  copy_cap?: number;
}) => request<{ job_id: string }>("POST", "/api/admin/decks/architect", args);

export const enqueueGenerateSlotCard = (args: {
  slug: string;
  slot_idx: number;
  brief: SlotBrief;
  k?: number;
}) =>
  request<{ job_id: string }>("POST", "/api/admin/decks/slot/generate", args);

export const getDeckState = (slug: string) =>
  request<DeckState>(
    "GET",
    `/api/admin/decks/state?slug=${encodeURIComponent(slug)}`,
  );

export type DeckStatePatch =
  | { set_chosen: { idx: number; card_id: string | null } }
  | { set_count: { idx: number; count: number } }
  | { set_brief: { idx: number; brief: Partial<SlotBrief> } };

export const patchDeckState = (slug: string, patch: DeckStatePatch) =>
  request<DeckState>("POST", "/api/admin/decks/state", { slug, patch });

export interface BuildDeckRating {
  status: "ok" | "rejected";
  slug: string;
  rating: number | null;
  win_rate: number | null;
  games_played: number;
  wins: number;
  draws: number;
  cohort_size: number;
  games_per_pair: number;
  elapsed_secs: number;
  error: { kind: string; message: string } | null;
}

export const rateDeck = (slug: string) =>
  request<BuildDeckRating>("POST", "/api/admin/decks/rate", { slug });

export interface FinalizeDeckResult {
  deck_path: string;
  cards: string[];
}

export const finalizeDeck = (args: {
  slug: string;
  npc_id: string;
  name?: string;
}) => request<FinalizeDeckResult>("POST", "/api/admin/decks/finalize", args);

export interface DeckRunSummary {
  slug: string;
  faction: string | null;
  name: string | null;
  deck_size: number | null;
  chosen: number;
  total: number;
  /** Filesystem mtime of `deck.json`, in posix seconds. */
  updated_at: number;
}

export const listDeckRuns = () =>
  request<{ runs: DeckRunSummary[] }>("GET", "/api/admin/decks/runs");

export const deleteDeckRun = (slug: string) =>
  request<{ slug: string; deleted: true }>(
    "DELETE",
    `/api/admin/decks/runs/${encodeURIComponent(slug)}`,
  );

export interface AdminPoolBucket {
  key: string;
  count: number;
}

export interface AdminPoolTriple {
  type: string;
  rarity: string;
  faction: string;
  count: number;
}

export interface AdminPoolStats {
  total: number;
  by_faction: AdminPoolBucket[];
  by_type: AdminPoolBucket[];
  by_cost: AdminPoolBucket[];
  by_rarity: AdminPoolBucket[];
  /** Sparse 3-axis breakdown the (type × rarity) × faction pivot table
   *  consumes. Only non-empty triples appear; the renderer fills zeros
   *  for the rest, which is what makes whole-rarity / whole-faction
   *  gaps (the "no uncommons left → empty_pool" failure mode) visible. */
  by_type_rarity_faction: AdminPoolTriple[];
}

export const getAdminCardStats = () =>
  request<{ pools: Record<AdminPool, AdminPoolStats> }>(
    "GET",
    "/api/admin/cards/stats",
  );

export interface AdminUser {
  id: number;
  username: string;
  is_admin: boolean;
  is_guest: boolean;
  is_banned: boolean;
  void_juice_ml: number;
  gold_coins: number;
  deck_slots: number;
  current_hp: number;
  cards_owned: number;
  /** Per-user toggleable factions (orcs / elves). The three default-
   *  unlocked factions (humans / goblins / dwarves) aren't in this
   *  map — they're always on and aren't user-revocable. */
  faction_unlocks: { orcs: boolean; elves: boolean };
}

export interface AdminUsersPage {
  users: AdminUser[];
  /** Total users matching the filter, across all pages. */
  total: number;
  /** 1-based page number echoed back by the server (clamped). */
  page: number;
  /** Page size echoed back by the server (clamped). */
  page_size: number;
}

export const listAdminUsers = (params?: {
  q?: string;
  page?: number;
  pageSize?: number;
}) => {
  const qs = new URLSearchParams();
  if (params?.q) qs.set("q", params.q);
  if (params?.page != null) qs.set("page", String(params.page));
  if (params?.pageSize != null) qs.set("page_size", String(params.pageSize));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<AdminUsersPage>("GET", `/api/admin/users${suffix}`);
};

export interface AdminItem {
  id: string;
  name: string;
}

export const listAdminItems = () =>
  request<{ items: AdminItem[] }>("GET", "/api/admin/items");

/** A row in the Grant Card submenu. `claimed_by_user_id` is non-null
 *  when some user already owns the card under the 1-of-1 model — those
 *  rows render disabled in the UI. */
export interface AdminGrantableCard {
  id: string;
  name: string;
  faction: string;
  rarity: string;
  claimed_by_user_id: number | null;
}

export const listGrantableCards = () =>
  request<{ cards: AdminGrantableCard[] }>("GET", "/api/admin/cards/grantable");

export interface RuntimeStats {
  current_matches: number;
  current_rooms: number;
  registered_users: number;
  online_users: number;
  peak_online_24h: number;
  uptime_seconds: number;
}

export const getRuntimeStats = () =>
  request<RuntimeStats>("GET", "/api/admin/runtime-stats");

export const grantCurrency = (
  userId: number,
  currency: "void_juice" | "gold",
  amount: number,
) =>
  request<{
    user_id: number;
    currency: string;
    granted: number;
    balances: { void_juice_ml: number; gold_coins: number };
  }>("POST", `/api/admin/users/${userId}/grant-currency`, { currency, amount });

/** Admin-only nuke. Deletes every CollectionEntry the user owns and
 *  moves any pack-claimed card directories back to `available/` so
 *  they can be re-rolled. Base-set cards stay where they are on disk
 *  (they were never under `claimed/`). */
export const resetCollection = (userId: number) =>
  request<{ user_id: number; rows_deleted: number; cards_unclaimed: number }>(
    "POST",
    `/api/admin/users/${userId}/reset-collection`,
  );

export const banUser = (userId: number, banned: boolean) =>
  request<{ user_id: number; is_banned: boolean }>(
    "POST",
    `/api/admin/users/${userId}/ban`,
    { banned },
  );

export const grantItem = (userId: number, itemId: string) =>
  request<{ user_id: number; item_id: string; granted: number }>(
    "POST",
    `/api/admin/users/${userId}/grant-item`,
    { item_id: itemId },
  );

export const grantCard = async (userId: number, cardId: string) => {
  const resp = await request<{
    user_id: number;
    card_id: string;
    granted: number;
  }>("POST", `/api/admin/users/${userId}/grant-card`, { card_id: cardId });
  // The admin may be granting to themselves; refreshing the shared
  // catalog cache is cheap relative to how rarely this runs.
  invalidateCatalog();
  return resp;
};

export const restoreHp = (userId: number) =>
  request<{ user_id: number; current_hp: number }>(
    "POST",
    `/api/admin/users/${userId}/restore-hp`,
  );

export const setFactionUnlock = (
  userId: number,
  faction: "orcs" | "elves",
  unlocked: boolean,
) =>
  request<{ user_id: number; faction: string; unlocked: boolean }>(
    "POST",
    `/api/admin/users/${userId}/set-faction-unlock`,
    { faction, unlocked },
  );

export type GenerateCardType = "creature" | "spell" | "enchantment" | "trap";
export type GenerateFaction =
  | "humans"
  | "goblins"
  | "dwarves"
  | "orcs"
  | "elves";
export type GenerateRarity = "common" | "uncommon" | "rare" | "legendary";

/** "fill" tells the server to resolve the axis to whichever value has
 *  the fewest cards in the live `cards/available/` pool (joint with any
 *  other "fill" axes). Resolution happens server-side in
 *  `Doi.Admin.PoolFill` before the generator job is enqueued. */
export type GenerateAxisChoice<Specific extends string> =
  | "fill"
  | "random"
  | Specific;

export const enqueueGenerate = (
  cardType?: GenerateAxisChoice<GenerateCardType>,
  faction?: GenerateAxisChoice<GenerateFaction>,
  rarity?: GenerateAxisChoice<GenerateRarity>,
) => {
  const body: Record<string, string> = {};
  // "random" is the wire word for "let the sampler pick" (sent as nil
  // server-side). Omit it from the body for back-compat — the server
  // already treats a missing field the same way.
  if (cardType && cardType !== "random") body.card_type = cardType;
  if (faction && faction !== "random") body.faction = faction;
  if (rarity && rarity !== "random") body.rarity = rarity;
  return request<{
    job_id: string;
    resolved: {
      card_type: string | null;
      faction: string | null;
      rarity: string | null;
    };
  }>(
    "POST",
    "/api/admin/generate",
    Object.keys(body).length === 0 ? undefined : body,
  );
};

export const listAdminJobs = () =>
  request<{ jobs: AdminJob[] }>("GET", "/api/admin/jobs");

/** One pack type's readiness snapshot. `packs_ready` is the
 *  conservative count of how many packs of this kind the current
 *  unclaimed pool can support. `status` is "blocked" when the pool
 *  can't fill even one pack, "below_target" while AutoGen is still
 *  filling, and "ready" once the buffer is met. */
export interface AdminPackReady {
  item_id: string;
  name: string;
  sprite: string;
  set: string;
  faction: string | null;
  packs_ready: number;
  commons: number;
  uncommons: number;
  rares: number;
  legendaries: number;
  target: number;
  status: "blocked" | "below_target" | "ready";
  unique_claims: boolean;
}

export const getAdminPacksReady = () =>
  request<{ pack_types: AdminPackReady[]; target: number }>(
    "GET",
    "/api/admin/packs-ready",
  );

/** AutoGen settings + 24h rolling spend per external provider. USD
 *  values come back as strings so we don't lose precision on the
 *  Decimal → JS-number conversion; the UI reads them with parseFloat
 *  for display only. */
/** Per-provider snapshot from ProviderControl (AIMD + breaker). The
 *  Generate tab renders one pill per provider, showing the effective
 *  cap (current AIMD floor), the admin-configured ceiling, and the
 *  breaker state. `claude_code` ceiling is hardcoded to 1 server-side. */
export interface ProviderControlSnapshot {
  provider: string;
  effective_cap: number;
  ceiling: number;
  breaker: "closed" | "open";
  /** Latched when the provider reported it's out of credits (HTTP
   *  402). Blocks all dispatch and pauses Auto-gen until the admin
   *  tops up and presses RESET. */
  credits_exhausted: boolean;
  credits_exhausted_at: string | null;
  backoff_remaining_ms: number;
  successes_since_step: number;
  last_throttle_at: string | null;
  last_failure_at: string | null;
  last_failure_reason: string | null;
}

export interface AdminAutoGenStatus {
  enabled: boolean;
  target_packs_per_type: number;
  openrouter: {
    cap_usd: string;
    spent_usd_24h: string;
    /** Ceiling for `ProviderControl`'s AIMD effective_cap. */
    concurrent_ceiling?: number;
    provider?: ProviderControlSnapshot;
  };
  pixellab: {
    cap_usd: string;
    spent_usd_24h: string;
    concurrent_ceiling?: number;
    provider?: ProviderControlSnapshot;
  };
  claude_code?: { provider?: ProviderControlSnapshot };
  last_tick_at: string | null;
  last_action: { kind: string; [k: string]: unknown } | null;
}

export const getAdminAutoGen = () =>
  request<AdminAutoGenStatus>("GET", "/api/admin/auto-gen");

export interface AdminAutoGenPatch {
  enabled?: boolean;
  target_packs_per_type?: number;
  openrouter_cap_usd?: string;
  pixellab_cap_usd?: string;
  openrouter_concurrent?: number;
  pixellab_concurrent?: number;
}

export const updateAdminAutoGen = (patch: AdminAutoGenPatch) =>
  request<AdminAutoGenStatus>("PUT", "/api/admin/auto-gen", patch);

/** Snapshot of every provider's AIMD + breaker. Backs the three pills
 *  on the Generate tab so the admin can see whether OpenRouter or
 *  PixelLab is throttled / blown. */
export interface AdminProviderControl {
  providers: Record<string, ProviderControlSnapshot>;
}

export const getAdminProviderControl = () =>
  request<AdminProviderControl>("GET", "/api/admin/provider-control");

export const resetAdminProviderBreaker = (provider: string) =>
  request<AdminProviderControl>(
    "POST",
    `/api/admin/provider-control/${provider}/reset`,
  );

export const tickAdminAutoGen = () =>
  request<{ action: { kind: string; [k: string]: unknown } | null }>(
    "POST",
    "/api/admin/auto-gen/tick",
  );

/** Admin cheat toggles. Stored in-memory on the server (`:persistent_term`),
 *  so they reset on server restart. Used to force a specific wildcard
 *  rarity into every pack while testing pack-opening flows. */
export interface AdminCheats {
  force_legendary: boolean;
  force_rare: boolean;
  /** When true, surfaces the Skip (ADMIN) button on the challenge
   *  fight modal for admins. Off by default and resets on server
   *  restart, so it can't accidentally persist into a real session. */
  admin_mode: boolean;
}

export const getAdminCheats = () =>
  request<AdminCheats>("GET", "/api/admin/cheats");

export const updateAdminCheats = (patch: Partial<AdminCheats>) =>
  request<AdminCheats>("PUT", "/api/admin/cheats", patch);

export interface AdminSkipChallengeResponse {
  ok: true;
  npc_id: string;
  set_id: string;
  index: number;
  rewards: NpcChallengeReward[];
  balances: { void_juice_ml: number; gold_coins: number };
  /** Same post-battle resume node the real battle path returns. Null
   *  means "fall through to the NPC's normal default-node
   *  resolution." */
  resume_node: string | null;
}

/** Admin-only "skip the battle and credit the win". The button that
 *  invokes this is gated by `me.is_admin && cheats.admin_mode`; the
 *  server enforces both as well. */
export const adminSkipChallenge = (
  npcId: string,
  setId: string,
  index: number,
) =>
  request<AdminSkipChallengeResponse>(
    "POST",
    `/api/admin/challenges/${encodeURIComponent(npcId)}/${encodeURIComponent(setId)}/${index}/skip`,
  );

/** Admin-only per-user flag toggle endpoints. PUT sets the flag to
 *  `value` (default 1); DELETE clears it. Both require Admin Mode
 *  server-side. The flag-toggle panel on each NPC page calls these. */
export const adminSetFlag = (flag: string, value: number = 1) =>
  request<{ ok: true; flag: string; value: number }>(
    "PUT",
    `/api/admin/flags/${encodeURIComponent(flag)}`,
    { value },
  );

export const adminUnsetFlag = (flag: string) =>
  request<{ ok: true; flag: string; value: null }>(
    "DELETE",
    `/api/admin/flags/${encodeURIComponent(flag)}`,
  );

export interface GenerateSettings {
  model: string;
  /** Model for the rules-validator pass. The validator itself always
   *  runs — every server-side generation is gated — so the only
   *  setting is which model judges the cards. */
  validator_model: string;
  prompt_version: string;
}

/** Server-wide defaults for the admin generate job. Stored in the
 *  server database so they survive Phoenix restarts. Routing is done
 *  by the Python tool: a bare id goes via Claude Code, an id with `/`
 *  goes via OpenRouter. */
export const getGenerateModel = () =>
  request<GenerateSettings>("GET", "/api/admin/generate-model");

export const setGenerateModel = (model: string) =>
  request<GenerateSettings>("PUT", "/api/admin/generate-model", { model });

export const getGenerateSettings = () =>
  request<GenerateSettings>("GET", "/api/admin/generate-settings");

export const updateGenerateSettings = (patch: Partial<GenerateSettings>) =>
  request<GenerateSettings>("PUT", "/api/admin/generate-settings", patch);

/** Server-cached read of OpenRouter's public model catalog. The
 *  picker autocompletes over this list. The server caches the
 *  upstream call so the picker is fast even on the first open. */
export interface OpenRouterCatalogModel {
  id: string;
  name: string | null;
  description: string | null;
  context_length: number | null;
}

export const listOpenRouterModels = () =>
  request<{ models: OpenRouterCatalogModel[] }>(
    "GET",
    "/api/admin/openrouter/models",
  );

// ---- admin: model comparison ----

/** Models the comparison tool offers. Mirrors the picker in
 *  `AdminPage.tsx` — change both together. The comparison job sends
 *  whatever string the UI selected, so we list canonical Anthropic
 *  model ids verbatim. */
export const COMPARE_MODEL_IDS = [
  "claude-opus-4-7",
  "claude-opus-4-6",
  "claude-sonnet-4-6",
  "claude-haiku-4-5-20251001",
] as const;

export type CompareModelId = (typeof COMPARE_MODEL_IDS)[number];

export interface CompareSpec {
  set_id: string;
  faction_id: string;
  rarity: string;
  cost: number;
  card_type: string;
  seed_words: string[];
  typicality: number;
}

/** Token-usage shape captured per model run by `compare_models.py`.
 *  Same four fields as `AdminJobUsage` minus `card_id`. `null` when
 *  the run pre-dates usage capture or the call failed before a
 *  ResultMessage arrived. */
export interface CompareModelUsage {
  input_tokens?: number;
  output_tokens?: number;
  cache_read_input_tokens?: number;
  cache_creation_input_tokens?: number;
}

/** Sonnet-backed second pass that judges whether the produced
 *  ``card.lua`` faithfully implements the produced ``rules_text``.
 *  - ok=true:  match
 *  - ok=false: mismatch — `reason` says how
 *  - ok=null:  the validator didn't run (no card.lua, errored, or
 *              pre-validator run); the UI falls back to the existing
 *              engine-validation badge. */
export interface CompareRulesValidation {
  ok: boolean | null;
  reason: string | null;
}

export interface CompareModelOutcome {
  model: string;
  /** For `compare_prompts` runs the model is constant and the
   *  varying axis is the prompt version. The viewer uses this label
   *  for the column header instead of the model id. `null` for
   *  legacy `compare_models` runs. */
  prompt_version?: string | null;
  slug: string;
  /** Set when the LLM call failed before producing any text (auth,
   *  model id rejected, transport crash). Distinct from `parse_error`,
   *  which means we got bytes that didn't decode. */
  error: string | null;
  parse_error: string | null;
  validation: {
    ok: boolean | null;
    error_kind: string | null;
    error_message: string | null;
  };
  timings: {
    llm: number;
    validation: number;
  };
  /** Token usage from this model's call. `null` for failed runs and
   *  for runs that pre-date usage capture. */
  usage: CompareModelUsage | null;
  /** USD estimate computed server-side from `usage` and the model's
   *  rates in `Doi.Admin.Pricing`. `null` when usage is missing or
   *  the model isn't priced. */
  cost_usd: number | null;
  /** Rules-vs-lua judgement (Sonnet 4.6 second pass). `null` when the
   *  pass didn't run for this model — older runs that pre-date the
   *  validator, or models that didn't produce a card.lua. */
  rules_validation: CompareRulesValidation | null;
  card_json: Record<string, unknown> | null;
  card_lua: string | null;
}

export type CompareRunKind = "compare_models" | "compare_prompts";

export interface CompareRunSummary {
  run_id: string;
  created_at: string;
  /** What axis varied across this run. Defaults to `compare_models`
   *  for legacy runs that pre-date the kind field. */
  kind: CompareRunKind;
  spec: CompareSpec;
  models: Array<{
    model: string;
    /** Set when `kind === "compare_prompts"`; `null` otherwise. */
    prompt_version?: string | null;
    slug: string;
    parse_ok: boolean;
    validation_ok: boolean | null;
    llm_seconds: number | null;
    /** Server-computed USD; `null` for runs without captured usage
     *  or for unpriced models. */
    cost_usd: number | null;
  }>;
}

export interface CompareRunDetail {
  run_id: string;
  /** What axis varied across this run. */
  kind: CompareRunKind;
  spec: CompareSpec;
  /** Each entry's `model` and `prompt_version` together identify the
   *  variant. `kind === "compare_models"` keeps `prompt_version` null;
   *  `kind === "compare_prompts"` keeps `model` constant across rows. */
  models: CompareModelOutcome[];
  /** Constant model used for every variant when
   *  `kind === "compare_prompts"`. `null` for `compare_models` runs. */
  model?: string | null;
  created_at: string | null;
}

export interface EnqueueCompareInput {
  models: string[];
  faction?: string;
  rarity?: string;
  card_type?: string;
  cost?: number;
  typicality?: number;
  seed?: number;
  seed_words?: string[];
  thinking?: boolean;
  /** Override the rules-validator (Sonnet-backed second pass) model.
   *  Same id space as the generator picker — bare ids route via
   *  Claude Code, slash ids via OpenRouter. Omitted = Python default
   *  (claude-sonnet-4-6). */
  validator_model?: string;
  /** Override the rules-validator's extended-thinking flag. Omitted =
   *  Python default (on). */
  validator_thinking?: boolean;
}

export const enqueueCompareModels = (input: EnqueueCompareInput) => {
  const body: Record<string, unknown> = { models: input.models };
  if (input.faction) body.faction = input.faction;
  if (input.rarity) body.rarity = input.rarity;
  if (input.card_type) body.card_type = input.card_type;
  if (typeof input.cost === "number") body.cost = input.cost;
  if (typeof input.typicality === "number") body.typicality = input.typicality;
  if (typeof input.seed === "number") body.seed = input.seed;
  if (input.seed_words && input.seed_words.length > 0)
    body.seed_words = input.seed_words;
  if (input.thinking) body.thinking = true;
  if (input.validator_model) body.validator_model = input.validator_model;
  if (typeof input.validator_thinking === "boolean")
    body.validator_thinking = input.validator_thinking;
  return request<{ job_id: string }>("POST", "/api/admin/compare-models", body);
};

export const listCompareRuns = () =>
  request<{ runs: CompareRunSummary[] }>("GET", "/api/admin/compare-runs");

export const getCompareRun = (runId: string) =>
  request<CompareRunDetail>(
    "GET",
    `/api/admin/compare-runs/${encodeURIComponent(runId)}`,
  );

// ---- admin: palette snap tool ----

/** POST a PNG to `/api/admin/tools/snap-palette` and get the
 *  Endesga-32-snapped PNG bytes back as a Blob. The server pipes the
 *  upload through `tools/snap_palette.py`. */
export async function snapPalette(file: File): Promise<Blob> {
  const form = new FormData();
  form.append("image", file);
  const r = await fetch("/api/admin/tools/snap-palette", {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!r.ok) {
    const ct = r.headers.get("content-type") ?? "";
    let msg = `HTTP ${r.status}`;
    let body: unknown = null;
    if (ct.includes("application/json")) {
      body = await r.json().catch(() => null);
      if (body && typeof body === "object" && "error" in body) {
        msg = String((body as { error: unknown }).error);
      }
    }
    throw new ApiError(msg, r.status, body);
  }
  return await r.blob();
}

// ---- admin: validator test ----

/** A single validation-set item plus what the model under test
 *  predicted for it. The same shape `tools/validator_eval.py` writes
 *  to disk in summary.json; consumed by the Validator Test admin
 *  tool's expandable items table. */
export interface ValidatorEvalItem {
  id: string;
  expected: { ok: boolean; reason: string };
  /** `ok=null` means the model didn't return a parsable verdict; the
   *  row is counted as wrong and `parse_error` carries the diagnostic. */
  predicted: { ok: boolean | null; reason: string | null };
  correct: boolean;
  parse_error: string | null;
  raw_reply: string;
  timings: { llm: number };
  usage: CompareModelUsage | null;
  /** Source card duplicated into the run summary so a row click can
   *  expand without a second fetch. */
  card_json: Record<string, unknown>;
  card_lua: string;
}

export interface ValidatorEvalScoreBlock {
  correct: number;
  total: number;
  pct: number;
}

export interface ValidatorEvalScores {
  overall: ValidatorEvalScoreBlock;
  /** Recall on truly-valid items (model correctly said valid). */
  valid_recall: ValidatorEvalScoreBlock;
  /** Recall on truly-invalid items (model correctly said invalid). */
  invalid_recall: ValidatorEvalScoreBlock;
}

export interface ValidatorEvalRunSummary {
  run_id: string;
  created_at: string;
  kind: "validator_eval";
  /** "completed" once `summary.json` is written; "running" while a
   *  job for this run is queued/active in the jobs server; "paused"
   *  when on-disk progress exists but no job is running for it
   *  (cancelled or crashed mid-run, awaiting Resume). The UI uses
   *  this to decide whether to render scores, a progress bar with
   *  Cancel, or a paused state with Resume / Delete. */
  status: "running" | "paused" | "completed";
  /** Present for "running" or "paused". `current` items processed of
   *  `total`. `phase` is "starting" between job dispatch and the
   *  first item — `total` will be 0 in that case. */
  progress: {
    current: number;
    total: number;
    phase: "starting" | "running" | "paused" | "done";
  } | null;
  model: string | null;
  thinking: boolean;
  /** Null while running or paused. */
  scores: ValidatorEvalScores | null;
  item_count: number;
  /** Total items in the dataset; with two-fold cross-validation
   *  every item is classified exactly once, so dataset_size also
   *  equals scores.overall.total. */
  dataset_size: number;
  /** Sizes of the two CV folds — half_a is the prompt for pass A
   *  and the test set for pass B; half_b is the reverse. */
  half_a_size: number;
  half_b_size: number;
  /** True when the run has on-disk checkpoint state that resume can
   *  pick up from. False for a fresh-start spec or a finished run. */
  resumable: boolean;
}

export interface ValidatorEvalRunDetail extends ValidatorEvalRunSummary {
  prompt_version: string;
  items: ValidatorEvalItem[];
}

/** Loaded once at boot. Lets the runs viewer/spec form know how
 *  many items the model has to process so cost + progress lines
 *  match the dataset on disk without round-tripping per render. */
export interface ValidatorEvalDatasetMeta {
  dataset_size: number;
}

export interface EnqueueValidatorEvalInput {
  /** Same id space as the model-compare picker — bare ids route via
   *  Claude Code, slash ids via OpenRouter. */
  model: string;
  thinking?: boolean;
  prompt_version?: string;
}

export const enqueueValidatorEval = (input: EnqueueValidatorEvalInput) => {
  const body: Record<string, unknown> = { model: input.model };
  if (typeof input.thinking === "boolean") body.thinking = input.thinking;
  if (input.prompt_version) body.prompt_version = input.prompt_version;
  return request<{ job_id: string }>("POST", "/api/admin/validator-eval", body);
};

/** Server-side cost preview for the Validator Test run. Returns
 *  `cost_usd: null` for models the rate table doesn't price (most
 *  non-Anthropic OpenRouter ids). `supports_caching` is the UI's cue
 *  to show "with prompt caching" / "no caching" alongside the dollar
 *  figure. */
export interface ValidatorEvalEstimate {
  model: string;
  supports_caching: boolean;
  validation_count: number;
  prefix_tokens: number;
  per_item_input_tokens: number;
  /** With extended thinking off this is a static estimate. With
   *  thinking on it's the measured `output_tokens` from the most
   *  recent `(model, prompt_version, thinking)` sample probe — the
   *  number that's actually used to compute `breakdown.output_usd`
   *  and `cost_usd`. `null` when thinking is on but no sample has
   *  been collected yet (`requires_sample === true`). */
  output_tokens_per_call: number | null;
  /** Whether thinking was enabled for this estimate. */
  thinking: boolean;
  /** True iff thinking is on but no probe has measured per-call
   *  output yet — the UI should surface a "Sample budget" action
   *  rather than a (necessarily fabricated) dollar figure. */
  requires_sample: boolean;
  cost_usd: number | null;
  breakdown: {
    cache_creation_usd: number;
    cache_read_usd: number;
    input_usd: number;
    output_usd: number;
  } | null;
  /** When a sample exists, the raw token-and-cost numbers from the
   *  one probe call so the UI can show "sample: 8400 out of which
   *  8200 reasoning, paid $0.04". `null` otherwise. */
  sample_token_breakdown: {
    input_tokens?: number;
    output_tokens?: number;
    reasoning_tokens?: number;
    cache_read_input_tokens?: number;
    cache_creation_input_tokens?: number;
    cost_usd?: number;
  } | null;
}

export const getValidatorEvalEstimate = (
  model: string,
  opts: { thinking?: boolean; promptVersion?: string } = {},
) => {
  const params = new URLSearchParams({ model });
  if (typeof opts.thinking === "boolean") {
    params.set("thinking", opts.thinking ? "true" : "false");
  }
  if (opts.promptVersion) params.set("prompt_version", opts.promptVersion);
  return request<ValidatorEvalEstimate>(
    "GET",
    `/api/admin/validator-eval/estimate?${params.toString()}`,
  );
};

/** Probe one model with one real call so the cost estimator can
 *  report an accurate per-call output-token count when extended
 *  thinking is on. The job runs `tools/sample_validator_call.py`
 *  via the admin job queue; its result is persisted on disk by
 *  `(model, prompt_version, thinking)` so subsequent estimate
 *  reads find it without re-billing. Returns the new job_id; the
 *  caller polls jobs + re-fetches the estimate when the job
 *  finishes. */
export const sampleValidatorEvalCall = (input: {
  model: string;
  thinking: boolean;
  promptVersion?: string;
}) => {
  const body: Record<string, unknown> = {
    model: input.model,
    thinking: input.thinking,
  };
  if (input.promptVersion) body.prompt_version = input.promptVersion;
  return request<{ job_id: string }>(
    "POST",
    "/api/admin/validator-eval/sample",
    body,
  );
};

/** Cancel a queued or running admin job. The server marks the job
 *  failed/cancelled, kills the Erlang Task, and force-kills the OS
 *  subprocess via `kill -KILL` (Erlang port closure alone doesn't
 *  reliably kill the OS process for jobs blocked on HTTP). For
 *  validator_eval, the run's `progress.json` and per-item
 *  `items.jsonl` checkpoint are PRESERVED so the run can later be
 *  resumed via `resumeValidatorEvalRun`. Returns 204 on success, 422
 *  if the job is already finished. */
export const cancelAdminJob = (jobId: string) =>
  request<void>("POST", `/api/admin/jobs/${encodeURIComponent(jobId)}/cancel`);

export const listValidatorEvalRuns = () =>
  request<{ runs: ValidatorEvalRunSummary[] }>(
    "GET",
    "/api/admin/validator-eval-runs",
  );

export const getValidatorEvalRun = (runId: string) =>
  request<ValidatorEvalRunDetail>(
    "GET",
    `/api/admin/validator-eval-runs/${encodeURIComponent(runId)}`,
  );

/** Resume a paused validator-eval run. Re-enqueues a fresh job
 *  pointed at the same run dir; the Python entrypoint reads
 *  `items.jsonl` and skips items it already classified, so the user
 *  only pays for the items that haven't been done yet. Returns the
 *  fresh job_id and the run_id. */
export const resumeValidatorEvalRun = (runId: string) =>
  request<{ job_id: string; run_id: string }>(
    "POST",
    `/api/admin/validator-eval-runs/${encodeURIComponent(runId)}/resume`,
  );

/** Delete a validator-eval run from disk. Refuses with 409 if a job
 *  for this run is still queued or running (cancel first). Returns
 *  204 on success. */
export const deleteValidatorEvalRun = (runId: string) =>
  request<void>(
    "DELETE",
    `/api/admin/validator-eval-runs/${encodeURIComponent(runId)}`,
  );

// ---- admin: prompt-version comparison ----

/** List of prompt-version directories on disk under
 *  `tools/cardgen_data/`. Populates the multi-select in the Prompt
 *  Compare tool. The default version is the one configured via
 *  `config :doi, :admin, :default_prompt_version`. */
export interface PromptVersionsResponse {
  versions: string[];
  default: string;
}

export const listPromptVersions = () =>
  request<PromptVersionsResponse>("GET", "/api/admin/prompt-versions");

export interface EnqueueComparePromptsInput {
  prompt_versions: string[];
  model: string;
  faction?: string;
  rarity?: string;
  card_type?: string;
  cost?: number;
  typicality?: number;
  seed?: number;
  seed_words?: string[];
  thinking?: boolean;
  /** Same semantics as `EnqueueCompareInput.validator_model`. */
  validator_model?: string;
  /** Same semantics as `EnqueueCompareInput.validator_thinking`. */
  validator_thinking?: boolean;
}

export const enqueueComparePrompts = (input: EnqueueComparePromptsInput) => {
  const body: Record<string, unknown> = {
    prompt_versions: input.prompt_versions,
    model: input.model,
  };
  if (input.faction) body.faction = input.faction;
  if (input.rarity) body.rarity = input.rarity;
  if (input.card_type) body.card_type = input.card_type;
  if (typeof input.cost === "number") body.cost = input.cost;
  if (typeof input.typicality === "number") body.typicality = input.typicality;
  if (typeof input.seed === "number") body.seed = input.seed;
  if (input.seed_words && input.seed_words.length > 0)
    body.seed_words = input.seed_words;
  if (input.thinking) body.thinking = true;
  if (input.validator_model) body.validator_model = input.validator_model;
  if (typeof input.validator_thinking === "boolean")
    body.validator_thinking = input.validator_thinking;
  return request<{ job_id: string }>(
    "POST",
    "/api/admin/compare-prompts",
    body,
  );
};

// ---- Playtest tab ----

export type PlaytestTemplateSummary = {
  id: string;
  name: string;
  /** POSIX seconds; server sorts newest-first but the field stays for tooltips/debug. */
  mtime: number;
};

export const listPlaytestTemplates = () =>
  request<{ templates: PlaytestTemplateSummary[] }>(
    "GET",
    "/api/admin/playtest/templates",
  );

/**
 * Replay the named Playtest template into a fresh battle. The server
 * force-ends the user's existing active game (Playtest is an admin
 * tool, so clobbering whatever they were playing is intended), creates
 * an engine session from the template, and returns the new battle's
 * `game_id` for the SPA to navigate to (`/battle/:game_id`).
 */
export const createPlaytestSession = (templateId: string) =>
  request<{ game_id: number }>("POST", "/api/admin/playtest/sessions", {
    template_id: templateId,
  });

// ---- admin: NPC decks ----

export interface AdminNpcSummary {
  id: string;
  name: string | null;
  faction: string | null;
  role: string | null;
}

export interface AdminNpcDeckSummary {
  slug: string;
  npc_id: string;
  name: string;
  faction: string;
  total: number;
  updated_at: string | null;
}

export interface AdminNpcDeckCardEntry {
  card_id: string;
  count: number;
  /** Full card detail inlined so the deck panel doesn't need the
   *  paginated catalog. `null` if the engine no longer knows the
   *  card (e.g. an authored file was deleted). */
  card: NpcDeckCatalogCard | null;
}

export interface AdminNpcDeck {
  slug: string;
  npc_id: string;
  name: string;
  faction: string;
  cards: AdminNpcDeckCardEntry[];
  updated_at: string | null;
}

export interface AdminNpcDeckInput {
  npc_id?: string;
  name: string;
  faction: string;
  slug?: string;
  cards: Array<[string, number]>;
}

export const listAdminNpcDecks = () =>
  request<{ npcs: AdminNpcSummary[]; decks: AdminNpcDeckSummary[] }>(
    "GET",
    "/api/admin/npc-decks",
  );

export const getAdminNpcDeck = (slug: string) =>
  request<{ deck: AdminNpcDeck }>(
    "GET",
    `/api/admin/npc-decks/${encodeURIComponent(slug)}`,
  );

export interface NpcDeckCatalogCard extends CatalogCard {
  pool: string;
  created_at: string | null;
  claimed_by_user_id: number | null;
}

export interface NpcDeckCatalogParams {
  page?: number;
  per_page?: number;
  pool?: AdminPool | "all";
  claim?: "all" | "unclaimed";
  sort?: "cost" | "created";
  q?: string;
  factions?: string;
  type?: string;
}

export const getAdminNpcDeckCatalog = (params: NpcDeckCatalogParams = {}) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<{
    cards: NpcDeckCatalogCard[];
    pools: string[];
    total: number;
    page: number;
    per_page: number;
  }>("GET", `/api/admin/npc-decks/catalog${suffix}`);
};

export const createAdminNpcDeck = (input: AdminNpcDeckInput) =>
  request<{ deck: AdminNpcDeck }>("POST", "/api/admin/npc-decks", {
    deck: input,
  });

export const updateAdminNpcDeck = (slug: string, input: AdminNpcDeckInput) =>
  request<{ deck: AdminNpcDeck }>(
    "PUT",
    `/api/admin/npc-decks/${encodeURIComponent(slug)}`,
    { deck: input },
  );

export const deleteAdminNpcDeck = (slug: string) =>
  request<void>("DELETE", `/api/admin/npc-decks/${encodeURIComponent(slug)}`);

/** Wipe the calling admin's challenge-completion rows for one NPC's
 *  challenge set. Single-user reset (admin-self), not a global wipe. */
export const resetAdminNpcChallenges = (npcId: string) =>
  request<{ npc_id: string; user_id: number; deleted: number }>(
    "POST",
    `/api/admin/npcs/${encodeURIComponent(npcId)}/reset-challenges`,
  );

// ---- balance pipeline ----
//
// Win-Participation-Rate (WPR) sweep. For every card, play many
// random-pile-vs-random-pile games and record how often the card's
// side wins. The card's raw "power %" is the per-card win rate;
// `adj_pp` is its deviation from a fixed 50% target, in percentage
// points.
//
// The sweep is a CPU-exclusive admin job. `evalCardBalance` is a
// synchronous single-card eval that shells out to the engine and
// returns within ~seconds at default N.

export interface EvalCardReport {
  card_id: string;
  faction: string;
  games: number;
  seed: number;
  wins: number;
  /** Win rate (0..1) of the card's faction deck *with* the candidate. */
  win_rate: number;
  /** Win rate (0..1) of the *same* deck with the candidate swapped out
   *  for an ordinary faction card, measured live against the same
   *  opponents. The comparison point for `adj_pp`. */
  baseline_win_rate: number;
  /** `(win_rate - baseline_win_rate) * 100`: the card's marginal effect
   *  on its deck's win rate, in percentage points. Faction strength
   *  cancels, so this isolates the single card. ~0 = pulls its weight. */
  adj_pp: number;
  elapsed_secs: number;
}

export const evalCardBalance = (cardId: string, games?: number) => {
  const body: Record<string, number> = {};
  if (games != null) body.games = games;
  return request<EvalCardReport>(
    "POST",
    `/api/admin/card-balance/${encodeURIComponent(cardId)}`,
    Object.keys(body).length === 0 ? undefined : body,
  );
};

export interface CardPowerEntry {
  card_id: string;
  card_faction: string;
  inclusions: number;
  wins: number;
  /** `wins / inclusions`. `null` when inclusions == 0. */
  win_rate: number | null;
}

export interface CardPower {
  games_played?: number;
  seed?: number;
  elapsed_secs?: number;
  cards: CardPowerEntry[];
  /** True when the file isn't on disk yet — sweep hasn't been run. */
  no_sweep_yet?: boolean;
}

/** Enqueue a card-power sweep job. CPU-exclusive: blocks all other
 *  admin jobs (and other sweep jobs) until it finishes. */
export const enqueueCardPowerSweep = (
  args: { games?: number; seed?: number } = {},
) => {
  const body: Record<string, number> = {};
  if (args.games != null) body.games = args.games;
  if (args.seed != null) body.seed = args.seed;
  return request<{ job_id: string }>(
    "POST",
    "/api/admin/card-power/sweep",
    Object.keys(body).length === 0 ? undefined : body,
  );
};

export const listCardPower = () =>
  request<CardPower>("GET", "/api/admin/card-power");

/** NPC variant of the card-power sweep: engine includes `npc_cards/`
 *  in its pool, response is filtered to only NPC-origin cards. Separate
 *  cache from the player sweep so the two sub-tabs stay independent. */
export const enqueueCardPowerNpcSweep = (
  args: { games?: number; seed?: number } = {},
) => {
  const body: Record<string, number> = {};
  if (args.games != null) body.games = args.games;
  if (args.seed != null) body.seed = args.seed;
  return request<{ job_id: string }>(
    "POST",
    "/api/admin/card-power/npc/sweep",
    Object.keys(body).length === 0 ? undefined : body,
  );
};

export const listCardPowerNpc = () =>
  request<CardPower>("GET", "/api/admin/card-power/npc");

// ---- deck-power (Bradley-Terry pool rating) ----
//
// Sweep plays a round-robin among NPC decks, starter decks, and a
// seeded random cohort, then fits BT so a strong deck that beats every
// random deck ~100% of the time still gets a finite, comparable rating
// (unlike raw win-rate, which would saturate). Ratings are anchored so
// the *global* random-deck mean is 0; each faction's median random
// rating is reported alongside so a deck can be compared to its own
// faction baseline rather than to all factions mixed together.

export type DeckSource = "npc" | "starter" | "random";

export interface DeckRating {
  id: string;
  source: DeckSource;
  faction: string;
  name: string;
  /** Global BT log-strength. 0 = mean random deck across the whole pool.
   *  Comparable across factions. */
  rating: number;
  /** Per-faction BT log-strength (same units as `rating`). 0 = mean
   *  random deck of *this* faction. `null` when the faction had too
   *  few decks to fit. Comparable only within a faction. */
  faction_rating: number | null;
  games_played: number;
  wins: number;
}

export interface SkippedAuthoredDeck {
  id: string;
  faction: string;
  name: string;
  /** Why the deck was dropped (usually a stale card-id reference). */
  reason: string;
}

export interface DeckPower {
  seed?: number;
  games_per_pair?: number;
  random_per_faction?: number;
  elapsed_secs?: number;
  pool_size?: number;
  decks: DeckRating[];
  /** Authored decks the engine dropped during the last sweep (e.g.,
   *  stale card ids). Empty when everything was legal. */
  skipped_authored?: SkippedAuthoredDeck[];
  /** True when the file isn't on disk yet — sweep hasn't been run. */
  no_sweep_yet?: boolean;
}

/** Enqueue a deck-power sweep job. CPU-exclusive: blocks all other
 *  admin jobs until it finishes. */
export const enqueueDeckPowerSweep = (
  args: {
    random_per_faction?: number;
    games_per_pair?: number;
    seed?: number;
  } = {},
) => {
  const body: Record<string, number> = {};
  if (args.random_per_faction != null)
    body.random_per_faction = args.random_per_faction;
  if (args.games_per_pair != null) body.games_per_pair = args.games_per_pair;
  if (args.seed != null) body.seed = args.seed;
  return request<{ job_id: string }>(
    "POST",
    "/api/admin/deck-power/sweep",
    Object.keys(body).length === 0 ? undefined : body,
  );
};

export const listDeckPower = () =>
  request<DeckPower>("GET", "/api/admin/deck-power");

export interface SandboxPlayInput {
  /** Player's deck. */
  deck: DeckRef;
  /** Opponent deck. `npc:<slug>` for a promoted NPC deck; `gen:<slug>`
   *  for a deck-gen output already staged into `cards/gen/`. */
  opp?: DeckRef;
  /** If supplied, the server first stages the scratch run at this
   *  path into `cards/gen/<slug>/` and `world/decks/gen/<slug>.yaml`,
   *  then plays against the resulting gen deck. Use this right after
   *  a deck-gen finishes, before promote. */
  run_root?: string;
  /** Optional `[seat0, seat1]` starting HP. Server default is [100, 100]. */
  starting_life?: [number, number];
  opp_label?: string;
}

/** Start an admin sandbox battle. One unified endpoint covering both
 *  the "promoted NPC deck" and "fresh deck-gen scratch" flows. Returns
 *  `{game_id}` for the SPA to navigate to `/battle/:game_id`. */
export const startSandboxBattle = (input: SandboxPlayInput) =>
  request<{ game_id: string | number }>(
    "POST",
    "/api/admin/decks/sandbox-play",
    input,
  );
